import type { LocalizedPair, PartialLocalizedPair } from '@diora/contracts';
import clsx from 'clsx';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocale } from '@/providers/locale';

/**
 * Both languages, side by side, in one control.
 *
 * This is the component that keeps "Arabic is not a translation pass" true once
 * other people are doing the editing. A single-language field with a language
 * switcher lets someone write English, move on, and never see the empty Arabic
 * box. Here the gap is on screen, marked, in the same glance as the value.
 *
 * Each side declares its own `lang` and `dir`, so Arabic types right-to-left
 * inside an English interface and vice versa. That is a property of the text
 * being typed, not of the person reading the dashboard.
 */
export function BilingualField({
  label,
  value,
  onChange,
  required,
  multiline,
  rows = 4,
  hint,
  maxLength,
  disabled,
  errorsAr,
  errorsEn,
  placeholder,
}: {
  label: ReactNode;
  /**
   * Nullable on purpose. A description written in only one language arrives
   * with `null` on the other side, and a component that assumes a string there
   * throws on the first record whose seed data happened to be complete.
   */
  value: PartialLocalizedPair;
  onChange: (next: LocalizedPair) => void;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  hint?: ReactNode;
  maxLength?: number;
  disabled?: boolean;
  errorsAr?: string[];
  errorsEn?: string[];
  placeholder?: LocalizedPair;
}) {
  const { t } = useTranslation();
  // The column below carries `direction: rtl` so Arabic types correctly. That
  // is right for the value and wrong for everything around it: the counter and
  // any error are interface prose, and rendering "16 of 100 characters" inside
  // an RTL box reorders it to "of 100 characters 16".
  const { dir: uiDir } = useLocale();
  const baseId = useId();

  // A null column and an empty box are the same thing to a person typing, and
  // what goes back to the API is always a string.
  const current: LocalizedPair = { ar: value.ar ?? '', en: value.en ?? '' };

  const sides = [
    {
      locale: 'ar' as const,
      dir: 'rtl' as const,
      tag: t('bilingual.arabic'),
      text: current.ar,
      errors: errorsAr,
      placeholder: placeholder?.ar,
      set: (next: string) => onChange({ ...current, ar: next }),
    },
    {
      locale: 'en' as const,
      dir: 'ltr' as const,
      tag: t('bilingual.english'),
      text: current.en,
      errors: errorsEn,
      placeholder: placeholder?.en,
      set: (next: string) => onChange({ ...current, en: next }),
    },
  ];

  return (
    <fieldset className="dio-field" disabled={disabled}>
      <legend className="dio-label">
        {label}
        {required && (
          <span className="dio-label__req" aria-label={t('common.required')}>
            *
          </span>
        )}
      </legend>

      <div className="dio-bilingual">
        {sides.map((side) => {
          const id = `${baseId}-${side.locale}`;
          // "Missing" is only a warning where the value is actually required.
          // Marking every empty optional field would make the signal worthless.
          const missing = required && side.text.trim().length === 0;
          const hasError = Boolean(side.errors?.length);

          return (
            <div
              key={side.locale}
              className={clsx('dio-bilingual__side', missing && 'dio-bilingual__side--missing')}
              lang={side.locale}
              dir={side.dir}
            >
              <label className="dio-bilingual__tag" htmlFor={id}>
                {side.tag}
                {missing && <span>· {t('bilingual.missing')}</span>}
              </label>

              {multiline ? (
                <textarea
                  id={id}
                  className="dio-textarea"
                  rows={rows}
                  value={side.text}
                  maxLength={maxLength}
                  placeholder={side.placeholder}
                  onChange={(event) => side.set(event.target.value)}
                  aria-invalid={hasError || undefined}
                />
              ) : (
                <input
                  id={id}
                  className="dio-input"
                  type="text"
                  value={side.text}
                  maxLength={maxLength}
                  placeholder={side.placeholder}
                  onChange={(event) => side.set(event.target.value)}
                  aria-invalid={hasError || undefined}
                />
              )}

              {hasError && (
                <p className="dio-error" role="alert" dir={uiDir}>
                  {side.errors?.join(' · ')}
                </p>
              )}

              {maxLength && (
                <p className="dio-hint tabular" dir={uiDir}>
                  {t('about.charactersUsed', { count: side.text.length, max: maxLength })}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {hint && <p className="dio-hint">{hint}</p>}
      {required && <p className="dio-hint">{t('bilingual.bothRequired')}</p>}
    </fieldset>
  );
}

/** The same control for a field that may legitimately stay empty. */
export function OptionalBilingualField(
  props: Omit<Parameters<typeof BilingualField>[0], 'value' | 'onChange'> & {
    value: PartialLocalizedPair | null;
    onChange: (next: LocalizedPair) => void;
  },
) {
  const { value, ...rest } = props;
  return <BilingualField {...rest} value={value ?? { ar: '', en: '' }} />;
}
